import { useEffect, useMemo, useState } from 'react';

import PaginationSection from '../../../common/PaginationSection';
import {
  checkboxColumn,
  marketColumn,
  orderDateColumn,
  ordererColumn,
  orderNoColumn,
  productNameColumn,
  productNoColumn,
  productOptionColumn,
  quantityColumn,
  receiverAddressColumn,
  receiverColumn,
  recevierMemo,
  salesAmountColumn,
} from '../orderTableColumnBlocks';

import EmptyFaceIcon from '@/assets/icons/EmptyFaceIcon.svg?react';
import { TooltipProvider } from '@/components/atoms/PcTooltip';
import { LayoutOrderColumn } from '@/pages/orders/_layouts/LayoutOrderColumn';
import { OrderItem } from '@/types/order-list';
import MarketTable from '../../../common/MarketTable';
import { useOrderDetails } from '../../OrderDetailsProvider';
import { deepEqual } from '@/utils/deep-equal';
import { DEFAULT_PAGE_SIZE } from '@/constants/order';

interface Props {
  page: number;
  onChangePage: (page: number) => void;
  totalPages: number;
  orderList: OrderItem[];
}

export function OrderTableColumn({ page, onChangePage, totalPages, orderList }: Props) {
  const { checkedOrders, setCheckedOrders, selectedMarket } = useOrderDetails();

  const selectableOrders = orderList.filter((order) => order.marketInfo.isOrderFurtherActionEnable);

  const allChecked =
    selectableOrders.length > 0 && checkedOrders.length === selectableOrders.length;

  const handleCheckAll = (checked: boolean) => {
    if (selectableOrders.length === 0) return; // 선택 가능한 주문이 없으면 아무 작업도 하지 않음
    setCheckedOrders(checked ? selectableOrders : []);
  };

  useEffect(() => {
    setCheckedOrders([]);
  }, [orderList.length]);

  const handleCheck = (order: OrderItem, checked: boolean) => {
    setCheckedOrders((prev) =>
      checked ? [...prev, order] : prev.filter((item) => !deepEqual(item, order)),
    );
  };

  const columns = useMemo(
    () => [
      ...(selectableOrders.length > 0
        ? [
            checkboxColumn({ allChecked, handleCheckAll, checkedOrders, handleCheck }),
            orderNoColumn,
          ]
        : []),
      marketColumn,
      orderDateColumn,
      productNameColumn,
      productOptionColumn,
      salesAmountColumn,
      quantityColumn,
      productNoColumn({ showSellerPageButton: true }),
      receiverColumn,
      receiverAddressColumn,
      recevierMemo,
      ordererColumn,
    ],
    [selectableOrders.length, allChecked, checkedOrders, selectedMarket],
  );

  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  return (
    <TooltipProvider>
      <LayoutOrderColumn className="relative">
        {/* MarketTable을 감싸는 div 추가 */}
        <div className=" min-h-0 overflow-auto">
          {/* ✅ 남은 공간 모두 차지 + 스크롤 */}
          <MarketTable
            orderList={orderList}
            columns={columns}
            columnOrder={columnOrder}
            setColumnOrder={setColumnOrder}
            checkedOrders={checkedOrders}
            setCheckedOrders={setCheckedOrders}
            pageIndex={page}
            pageSize={DEFAULT_PAGE_SIZE}
            pageCount={Math.ceil(orderList.length / DEFAULT_PAGE_SIZE)} // 클라이언트사이드 시
          />
          {orderList.length === 0 && (
            <div className="absolute inset-0 top-[40px] z-[50] flex flex-col items-center justify-center gap-[20px] rounded-[20px] bg-white">
              <EmptyFaceIcon />
              <p className="text-gr-500">검색결과가 없어요.</p>
            </div>
          )}
        </div>

        {/* 페이지네이션 컨트롤 */}
        <div className="flex h-[70px] max-h-[70px] items-center justify-center gap-4 border-t">
          <PaginationSection page={page} onChangePage={onChangePage} totalPages={totalPages} />
        </div>
      </LayoutOrderColumn>
    </TooltipProvider>
  );
}
